import { getAnonymousProfile } from "@/utils/anonymousProfile";
import type { AnonymousProfile } from "@/utils/anonymousProfile";
import { MessageCircleIcon } from "lucide-react";
import { useCallback, useState } from "react";

import { AnonymousProfileModal } from "../AnonymousProfileModal";

interface AnonymousProfileToggleProps {
    isLoggedIn: boolean;
    isEnabled: boolean;
    totalCount: number;
    onToggle: () => void;
    onProfileUpdate: (profile: AnonymousProfile, enabled: boolean) => void;
    onSignIn: () => void;
}

/**
 * 익명 프로필 토글 컴포넌트
 * 첫 사용 시 모달 표시, 이후에는 직접 토글
 */
export const AnonymousProfileToggle: React.FC<AnonymousProfileToggleProps> = ({
    isLoggedIn,
    isEnabled,
    totalCount,
    onToggle,
    onProfileUpdate,
    onSignIn
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleToggle = useCallback(() => {
        if (!isLoggedIn) {
            onSignIn();
            return;
        }

        if (isEnabled) {
            onToggle();
        } else {
            const profile = getAnonymousProfile();
            if (!profile) {
                setIsModalOpen(true);
            } else {
                onToggle();
            }
        }
    }, [isEnabled, isLoggedIn, onSignIn, onToggle]);

    const handleModalConfirm = useCallback(
        (profile: AnonymousProfile, enabled: boolean) => {
            onProfileUpdate(profile, enabled);
            setIsModalOpen(false);
        },
        [onProfileUpdate]
    );

    return (
        <>
            <div className="mb-4 flex items-center gap-2">
                <div
                    className="flex items-center gap-2 text-base font-semibold
                        text-gray-900 dark:text-white"
                >
                    <MessageCircleIcon className="size-5" /> 댓글 {totalCount}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleToggle}
                        className="flex cursor-pointer items-center gap-2
                            rounded-md bg-gray-100 p-1.5 transition-colors
                            hover:bg-gray-200 dark:bg-gray-700
                            dark:hover:bg-gray-600"
                    >
                        <span
                            className="select-none text-sm font-medium
                                text-gray-700 dark:text-gray-300"
                        >
                            {isEnabled
                                ? "내 프로필로 작성하기"
                                : "익명으로 작성하기"}
                        </span>
                    </button>
                </div>
            </div>

            <AnonymousProfileModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleModalConfirm}
            />
        </>
    );
};
